import { NgModule, ErrorHandler, APP_INITIALIZER, Injectable } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ActivationService, CoreDataModule, PersistentStorageService, ReduxStoreModule } from '@monorepo/core-data';
import { environment } from '../environments/environment';
import { HttpInterceptorService } from './http.intercepter.service';
import { IonicStorageModule } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { Zip } from '@ionic-native/zip/ngx';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslationLoaderFactory, CustomLoggerDbService } from '@monorepo/services';
import { LoggerModule } from 'ngx-logger';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';

export function environmentFactory() {
  return environment;
}

// Simple error handler for initial bootstrapping
class BootstrapErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    console.error('Bootstrap error:', error);
  }
}

// Service to manage error handler state
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private errorHandler: ErrorHandler = new BootstrapErrorHandler();

  setErrorHandler(handler: ErrorHandler) {
    this.errorHandler = handler;
  }

  handleError(error: unknown) {
    this.errorHandler.handleError(error);
  }
}

// Factory function to create error handler
export function errorHandlerFactory(errorHandlerService: ErrorHandlerService) {
  return {
    handleError: (error: unknown) => errorHandlerService.handleError(error),
  };
}

// Initialize logger before app starts
export function initializeLogger(loggerDbService: CustomLoggerDbService) {
  return () => {
    console.log('Initializing logger DB');
    return loggerDbService.init();
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    CoreDataModule,
    ReduxStoreModule,
    LoggerModule.forRoot(environment.logging),
    IonicStorageModule.forRoot(),
    IonicStorageModule.forRoot({
      name: '__factoryDatabae',
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
    }),
    IonicStorageModule.forRoot({
      name: '__logDatabase',
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => {
          const factory = new TranslationLoaderFactory(http);
          return factory.createForApp('input-app');
        },
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    FileOpener,
    { provide: 'Zip', useClass: Zip },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: 'environment',
      useFactory: environmentFactory,
      deps: [],
    },
    HttpClient,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    InAppBrowser,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLogger,
      deps: [CustomLoggerDbService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (persistantStorage: PersistentStorageService) => () => persistantStorage.init(),
      deps: [PersistentStorageService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (activationService: ActivationService) => () => activationService.init(), // inline call
      deps: [ActivationService],
      multi: true,
    },
    ErrorHandlerService,
    {
      provide: ErrorHandler,
      useFactory: errorHandlerFactory,
      deps: [ErrorHandlerService],
    },
  ],
  bootstrap: [AppComponent],
  exports: [],
})
export class AppModule {}
